/**
 * 认知扭曲动画
 * Cognitive Distortions Animation - 心理学教育
 * 
 * 核心效果：
 * 1. 展示常见的思维谬误（灾难化、二元对立、情绪推理等）
 * 2. 可视化负面思维模式及其调整
 * 3. 思维气泡、对比展示、转场效果
 * 4. CBT 治疗常用工具
 * 
 * 视觉规范：
 * - 背景：深黑 #0A0E27
 * - 主色：暖橙 #FF6B35
 * - 辅色：冷青 #4ECDC4
 * - 大标题：80px
 * 
 * 时长：360 帧（12秒 @ 30fps）
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

type CognitiveDistortion = {
  id: string;
  name: string;
  nameEn: string;
  icon: string;
  distortion: string;        // 扭曲思维
  rational: string;          // 理性思维
  example: string;           // 示例
  color: string;
  adjustment: string;        // 调整方法
};

type CognitiveDistortionsAnimationProps = {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
};

// ==================== 数据配置 ====================

const COGNITIVE_DISTORTIONS: CognitiveDistortion[] = [
  {
    id: 'catastrophizing',
    name: '灾难化',
    nameEn: 'Catastrophizing',
    icon: '🌊',
    distortion: '一点小失误 → 灾难即将来临',
    rational: '这只是挫折，可以补救',
    example: '考试失败 → 人生完蛋',
    color: '#EF4444',
    adjustment: '问自己：最坏结果真的会发生吗？有什么证据？',
  },
  {
    id: 'black-white',
    name: '二元对立',
    nameEn: 'All-or-Nothing',
    icon: '⚫⚪',
    distortion: '非黑即白，没有中间地带',
    rational: '大多数情况在灰色地带',
    example: '不完美 → 完全失败',
    color: '#8B5CF6',
    adjustment: '寻找中间选项：70% 也是成功',
  },
  {
    id: 'emotional-reasoning',
    name: '情绪推理',
    nameEn: 'Emotional Reasoning',
    icon: '💭',
    distortion: '我感觉到了 → 它一定是真的',
    rational: '感受 ≠ 事实',
    example: '感觉焦虑 → 一定有危险',
    color: '#F59E0B',
    adjustment: '区分感受和事实：记录客观证据',
  },
  {
    id: 'mind-reading',
    name: '读心术',
    nameEn: 'Mind Reading',
    icon: '🔮',
    distortion: '我知道他们在想什么',
    rational: '我无法知道他人想法',
    example: '他没回复 → 肯定讨厌我',
    color: '#EC4899',
    adjustment: '基于证据判断，而非假设',
  },
  {
    id: 'overgeneralization',
    name: '过度泛化',
    nameEn: 'Overgeneralization',
    icon: '🔄',
    distortion: '一次失败 → 永远失败',
    rational: '每次情况都不同',
    example: '一次被拒 → 永远不会被爱',
    color: '#06B6D4',
    adjustment: '用"有时候"替代"总是"',
  },
  {
    id: 'should-statements',
    name: '应该陈述',
    nameEn: 'Should Statements',
    icon: '📋',
    distortion: '我必须/应该/一定要',
    rational: '我选择/想要/希望',
    example: '我必须完美 → 焦虑压力',
    color: '#10B981',
    adjustment: '用"想要"替代"应该"，减少自我批评',
  },
];

// ==================== 组件定义 ====================

export const CognitiveDistortionsAnimation: React.FC<CognitiveDistortionsAnimationProps> = ({
  primaryColor = '#FF6B35',
  secondaryColor = '#4ECDC4',
  backgroundColor = '#0A0E27',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // ===== 动画时间轴 =====
  
  // 标题淡入 (0-30 帧)
  const titleOpacity = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  const titleScale = interpolate(
    spring({
      frame: frame - 5,
      fps,
      config: { damping: 12, stiffness: 80 },
    }),
    [0, 1],
    [0.9, 1]
  );

  // 当前展示的扭曲索引（每40帧切换一个）
  const currentDistortionIndex = Math.min(
    Math.floor(Math.max(0, frame - 40) / 40),
    COGNITIVE_DISTORTIONS.length - 1
  );
  const currentDistortion = COGNITIVE_DISTORTIONS[currentDistortionIndex];

  // 思维气泡动画
  const bubbleScale = spring({
    frame: frame - (40 + currentDistortionIndex * 40),
    fps,
    config: { damping: 15, stiffness: 100, mass: 0.5 },
  });

  // 转换箭头动画
  const arrowOpacity = interpolate(
    frame - (40 + currentDistortionIndex * 40),
    [0, 20, 40],
    [0, 1, 1],
    { extrapolateRight: 'clamp' }
  );

  // 理性思维延迟出现
  const rationalOpacity = interpolate(
    frame - (40 + currentDistortionIndex * 40 + 15),
    [0, 20],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // 底部总结 (320-360 帧)
  const summaryOpacity = interpolate(frame, [320, 350], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* 主标题 */}
      <div
        style={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: `translateX(-50%) scale(${titleScale})`,
          textAlign: 'center',
          opacity: titleOpacity,
        }}
      >
        <h1
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: primaryColor,
            margin: 0,
            fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          }}
        >
          认知扭曲
        </h1>
        <p
          style={{
            fontSize: 28,
            color: '#9CA3AF',
            marginTop: 8,
            fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          }}
        >
          Cognitive Distortions · CBT 认知行为治疗核心概念
        </p>
      </div>

      {/* 主内容区域 */}
      {frame > 35 && currentDistortion && (
        <div
          style={{
            position: 'absolute',
            top: '22%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '90%',
            maxWidth: 1600,
          }}
        >
          {/* 扭曲标题 */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: 40,
              opacity: bubbleScale,
            }}
          >
            <div style={{ fontSize: 72, marginBottom: 16 }}>{currentDistortion.icon}</div>
            <h2
              style={{
                fontSize: 58,
                fontWeight: 'bold',
                color: currentDistortion.color,
                margin: 0,
                fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
              }}
            >
              {currentDistortion.name}
            </h2>
            <p
              style={{
                fontSize: 28,
                color: secondaryColor,
                marginTop: 8,
                fontFamily: 'Arial, sans-serif',
              }}
            >
              {currentDistortion.nameEn}
            </p>
          </div>

          {/* 思维对比展示 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 60,
              marginTop: 50,
            }}
          >
            {/* 左侧：扭曲思维气泡 */}
            <div
              style={{
                flex: 1,
                maxWidth: 700,
                opacity: bubbleScale,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  backgroundColor: `${currentDistortion.color}15`,
                  borderRadius: 24,
                  padding: '40px 36px',
                  border: `3px solid ${currentDistortion.color}`,
                  boxShadow: `0 8px 32px ${currentDistortion.color}30`,
                }}
              >
                {/* 气泡尖角 */}
                <div
                  style={{
                    position: 'absolute',
                    right: -15,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderLeft: `15px solid ${currentDistortion.color}`,
                  }}
                />

                {/* 标题 */}
                <h3
                  style={{
                    fontSize: 28,
                    fontWeight: 'bold',
                    color: currentDistortion.color,
                    margin: '0 0 20px 0',
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  🚫 扭曲思维
                </h3>

                {/* 扭曲内容 */}
                <p
                  style={{
                    fontSize: 26,
                    color: '#E5E7EB',
                    margin: 0,
                    lineHeight: 1.6,
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  {currentDistortion.distortion}
                </p>

                {/* 示例 */}
                <div
                  style={{
                    marginTop: 20,
                    padding: '16px 20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: 12,
                    border: `1px solid ${currentDistortion.color}40`,
                  }}
                >
                  <p
                    style={{
                      fontSize: 24,
                      color: '#CBD5E1',
                      margin: 0,
                      fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                    }}
                  >
                    💭 {currentDistortion.example}
                  </p>
                </div>
              </div>
            </div>

            {/* 中间：转换箭头 */}
            <div
              style={{
                fontSize: 72,
                opacity: arrowOpacity,
                animation: 'pulse 2s infinite',
              }}
            >
              →
            </div>

            {/* 右侧：理性思维气泡 */}
            <div
              style={{
                flex: 1,
                maxWidth: 700,
                opacity: rationalOpacity,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  backgroundColor: '#10B98115',
                  borderRadius: 24,
                  padding: '40px 36px',
                  border: '3px solid #10B981',
                  boxShadow: '0 8px 32px #10B98130',
                }}
              >
                {/* 气泡尖角 */}
                <div
                  style={{
                    position: 'absolute',
                    left: -15,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderRight: '15px solid #10B981',
                  }}
                />

                {/* 标题 */}
                <h3
                  style={{
                    fontSize: 28,
                    fontWeight: 'bold',
                    color: '#10B981',
                    margin: '0 0 20px 0',
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  ✅ 理性思维
                </h3>

                {/* 理性内容 */}
                <p
                  style={{
                    fontSize: 26,
                    color: '#E5E7EB',
                    margin: 0,
                    lineHeight: 1.6,
                    fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                  }}
                >
                  {currentDistortion.rational}
                </p>

                {/* 调整方法 */}
                <div
                  style={{
                    marginTop: 20,
                    padding: '16px 20px',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: 12,
                    border: '1px solid #10B98140',
                  }}
                >
                  <p
                    style={{
                      fontSize: 24,
                      color: '#CBD5E1',
                      margin: 0,
                      fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
                    }}
                  >
                    💡 {currentDistortion.adjustment}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 进度指示器 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              marginTop: 80,
            }}
          >
            {COGNITIVE_DISTORTIONS.map((distortion, index) => (
              <div
                key={distortion.id}
                style={{
                  width: index === currentDistortionIndex ? 50 : 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor:
                    index === currentDistortionIndex
                      ? distortion.color
                      : 'rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 底部总结 */}
      <div
        style={{
          position: 'absolute',
          bottom: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          opacity: summaryOpacity,
        }}
      >
        <p
          style={{
            fontSize: 26,
            color: '#9CA3AF',
            margin: 0,
            fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
          }}
        >
          识别扭曲思维 · 培养理性思考 · 改善心理健康
        </p>
      </div>

      {/* 装饰性元素 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          opacity: 0.08,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 200,
            height: 200,
            background: `radial-gradient(circle, ${primaryColor} 0%, transparent 70%)`,
            filter: 'blur(50px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '25%',
            right: '12%',
            width: 180,
            height: 180,
            background: `radial-gradient(circle, ${secondaryColor} 0%, transparent 70%)`,
            filter: 'blur(45px)',
          }}
        />
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </AbsoluteFill>
  );
};

// ==================== 导出默认配置 ====================

export const defaultCognitiveDistortionsProps: CognitiveDistortionsAnimationProps = {
  primaryColor: '#FF6B35',
  secondaryColor: '#4ECDC4',
  backgroundColor: '#0A0E27',
};
